import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import DuplicateEmailAlert from "../Utilities/DuplicateEmailAlert";
import { registerFormInnerSx } from "../RegisterPageScaffold";
import {
  registerPrimaryColor,
  registerSecondaryColor,
  registerOutlinedFieldSx,
} from "../registerUiConstants";

const LABEL = {
  email_address: "Email Address",
  email: "Email Address",
  username: "Username",
  name: "Full Name",
  phonenumber: "Phone Number",
  phone_number: "Phone Number",
  password: "Password",
  confirmPassword: "Confirm Password",
};

const DEFAULT_HINT = {
  email_address: "Enter your email address",
  email: "Enter your email address",
  username: "Enter a unique username",
  name: "Enter your full name",
  phonenumber: "Enter your phone number",
  phone_number: "Enter your phone number",
  password:
    "Enter a strong password with at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character",
  confirmPassword: "Confirm your password by entering it again",
};

const PASSWORD_HELPER =
  "Password must be 8 characters, one uppercase, one lowercase, one number and one special character";

function hintFor(cfg, field) {
  if (cfg.hints?.[field]) return cfg.hints[field];
  return DEFAULT_HINT[field] || "";
}

const PLACEHOLDER = {
  email_address: "Enter your email address here",
  email: "Enter your email address here",
  username: "Enter your username here",
  name: "Enter your full name here",
  phonenumber: "Enter your phone number here",
  password: "Enter your password here",
  confirmPassword: "Confirm your password here",
};

function InternalInfoBanner({ primaryColor, secondaryColor }) {
  return (
    <Box
      data-testid="register-internal-info-banner"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        background: "linear-gradient(135deg, #FFFBF0 0%, #F8F0FF 100%)",
        color: "#6B4F1D",
        borderRadius: "12px",
        p: 3,
        mb: 3,
        boxShadow: "0 8px 32px rgba(51, 51, 51, 0.1)",
        border: "1px solid rgba(51, 51, 51, 0.2)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "100%",
          background: `linear-gradient(to bottom, ${primaryColor}, ${secondaryColor})`,
          borderRadius: "12px 0 0 12px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "48px",
          height: "48px",
          borderRadius: "12px",
          background: "rgba(51, 51, 51, 0.1)",
          mr: 3,
          flexShrink: 0,
        }}
      >
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="12" fill="#333333" />
          <path d="M12 7v5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="16" r="1" fill="#FFFFFF" />
        </svg>
      </Box>
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, fontSize: "1.1rem", color: "#333333" }}
        >
          Important Registration Information
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#333333", lineHeight: 1.6, fontSize: "0.95rem" }}
        >
          Please remember your{" "}
          <strong style={{ color: "#666666" }}>username</strong>
          {""} as it will be used for login.
          <br />
          <span style={{ color: "#666666", fontWeight: 600 }}>
            Username cannot be an email address.
          </span>
        </Typography>
      </Box>
    </Box>
  );
}

InternalInfoBanner.propTypes = {
  primaryColor: PropTypes.string.isRequired,
  secondaryColor: PropTypes.string.isRequired,
};

function getFieldPlaceholder(variantId, field) {
  if (variantId === "pgContact" && field === "confirmPassword") {
    return "Re-enter your password here";
  }
  return PLACEHOLDER[field];
}

function getFieldRawErr(errs, field) {
  if (field === "email_address") return errs.email_address || "";
  return errs[field] || "";
}

function getFieldType(field, isPw, isConf, showPassword, confirmShow) {
  if (isPw) return showPassword ? "text" : "password";
  if (isConf) return confirmShow ? "text" : "password";
  if (field === "email" || field === "email_address") return "email";
  return "text";
}

function getFieldHelperText(isPw, isConf, errs, fd, rawErr) {
  if (isPw) {
    if (errs.password) return errs.password;
    return fd.password ? PASSWORD_HELPER : "";
  }
  if (isConf) return errs.confirmPassword || "";
  return rawErr || "";
}

function getFieldOutlineErr(isPw, isConf, errs, rawErr) {
  if (isPw) return !!errs.password;
  if (isConf) return !!errs.confirmPassword;
  return !!rawErr;
}

function getFieldHelperColor(isPw, isConf, errs) {
  if ((isPw && errs.password) || (isConf && errs.confirmPassword)) {
    return "#d32f2f";
  }
  return "text.secondary";
}

export default function RegisterStandardFormBody({ reg, showPgPhoneMask }) {
  const navigate = useNavigate();
  const { config, variantId } = reg;
  const primary = registerPrimaryColor;
  const secondary = registerSecondaryColor;
  const errs = reg.errors || {};
  const fd = reg.formData || {};
  const showSecondaryNav = config.showSecondaryNavButton !== false;

  const confirmCv = () => {
    const show = config.confirmPasswordShareVisibility
      ? reg.showPassword
      : reg.showConfirmPassword;
    const setShow = config.confirmPasswordShareVisibility
      ? reg.setShowPassword
      : reg.setShowConfirmPassword;
    return { show, setShow };
  };

  const submitDisabled =
    (typeof reg.duplicateEmailCheck?.isSubmitDisabled === "function"
      ? reg.duplicateEmailCheck.isSubmitDisabled(
          reg.loading,
          variantId === "pgContact" && !reg.effectiveToken
        )
      : reg.loading ||
        (config.useDuplicateEmailCheck && reg.duplicateEmailCheck?.hasDuplicate) ||
        (variantId === "pgContact" && !reg.effectiveToken));

  const emailForDupAlert =
    config.emailField === "email_address" ? fd.email_address : fd.email;

  const dupAfterEmail =
    config.duplicateEmailPlacement === "afterEmail" &&
    config.useDuplicateEmailCheck;
  const dupBeforeSubmit =
    config.duplicateEmailPlacement === "beforeSubmit" &&
    config.useDuplicateEmailCheck;

  const dupUi = (
    <Box data-testid="register-duplicate-email-alert">
      <DuplicateEmailAlert
        {...(reg.duplicateEmailCheck.getAlertProps
          ? reg.duplicateEmailCheck.getAlertProps(emailForDupAlert)
          : {
              accounts: reg.duplicateEmailCheck.existingAccounts,
              email: emailForDupAlert,
            })}
      />
    </Box>
  );

  const blurFor = (field) => {
    switch (field) {
      case "email":
      case "email_address":
        return reg.onEmailBlurDupCheck;
      case "username":
        return () => reg.validateUsername();
      case "name":
        return () => reg.validateName();
      case "phonenumber":
        return () => reg.validatePhonenumber();
      case "password":
        return () => reg.validatePassword();
      case "confirmPassword":
        return () => reg.validateConfirmPassword();
      default:
        return undefined;
    }
  };

  const renderMaskedPhonePg = () => {
    const hasErr = !!errs.phone_number;
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {LABEL.phone_number}
          </Typography>
          <Tooltip title={hintFor(config, "phone_number")} placement="top" arrow>
            <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <InputMask
          mask="+1-999-999-9999"
          value={fd.phone_number}
          onChange={reg.handleInputChange}
          onBlur={() => reg.validatePhonePg()}
        >
          {(inputProps) => (
            <TextField
              {...inputProps}
              fullWidth
              variant="outlined"
              name="phone_number"
              required
              placeholder="+1-555-123-4567"
              error={hasErr}
              helperText={errs.phone_number || ""}
              sx={registerOutlinedFieldSx(primary, hasErr)}
              onKeyDown={reg.handleKeyDown}
              inputProps={{ ...inputProps.inputProps, 'data-testid': 'register-phone_number' }}
            />
          )}
        </InputMask>
      </>
    );
  };

  const renderVisibilityAdornment = (show, setShow) => (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShow(!show)}
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
        sx={{ color: "text.secondary" }}
      >
        {show ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    </InputAdornment>
  );

  const renderTextFieldRow = (field) => {
    const label = LABEL[field];
    const tooltip = hintFor(config, field);
    const placeholder = getFieldPlaceholder(variantId, field);
    const rawErr = getFieldRawErr(errs, field);

    const isPw = field === "password";
    const isConf = field === "confirmPassword";
    const cv = isConf ? confirmCv() : null;

    const textFieldType = getFieldType(
      field,
      isPw,
      isConf,
      reg.showPassword,
      cv?.show
    );

    const helperText = getFieldHelperText(isPw, isConf, errs, fd, rawErr);

    const adorn = isPw || isConf;
    let endAd;
    if (isPw) {
      endAd = renderVisibilityAdornment(reg.showPassword, reg.setShowPassword);
    } else if (isConf) {
      endAd = renderVisibilityAdornment(cv.show, cv.setShow);
    }

    const outlineErr = getFieldOutlineErr(isPw, isConf, errs, rawErr);

    const helperColor = getFieldHelperColor(isPw, isConf, errs);

    const baseSx = {
      ...registerOutlinedFieldSx(primary, outlineErr),
      ...((isPw || isConf) && {
        "& .MuiFormHelperText-root": {
          margin: "4px 0 0 0",
          fontSize: "0.75rem",
          color: helperColor,
        },
      }),
    };

    return (
      <React.Fragment key={field}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            {label}
          </Typography>
          <Tooltip title={tooltip} placement="top" arrow>
            <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          name={field}
          type={textFieldType}
          required
          value={fd[field] != null ? fd[field] : ""}
          onChange={reg.handleInputChange}
          onKeyDown={reg.handleKeyDown}
          onBlur={blurFor(field)}
          placeholder={placeholder}
          error={outlineErr}
          helperText={helperText}
          sx={baseSx}
          slotProps={{
            ...(adorn ? { input: { endAdornment: endAd } } : {}),
            htmlInput: {
              "data-testid": `register-${field}`,
              ...(field === "phonenumber" ? { autoComplete: "tel" } : {}),
            },
          }}
        />
      </React.Fragment>
    );
  };

  const chunks = [];

  config.fieldOrder.forEach((field) => {
    chunks.push(<React.Fragment key={field}>{renderTextFieldRow(field)}</React.Fragment>);
    if (dupAfterEmail && field === config.emailField) {
      chunks.push(<Box key={`_${field}_dup`}>{dupUi}</Box>);
    }
    if (variantId === "pgContact" && showPgPhoneMask && field === "email") {
      chunks.push(<React.Fragment key="_pg_phone">{renderMaskedPhonePg()}</React.Fragment>);
    }
  });

  if (dupBeforeSubmit) {
    chunks.push(<Box key="dup_bs">{dupUi}</Box>);
  }

  return (
    <Box sx={registerFormInnerSx}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box
          component="img"
          src="/ProbateBondPros.png"
          alt="Probate Bond Pros Logo"
          sx={{
            height: 60,
            maxWidth: "100%",
            filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.2))",
          }}
        />
      </Box>

      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Box
          sx={{
            position: "absolute",
            top: 40,
            left: 0,
            "@media (max-width: 768px)": {
              left: -10,
            },
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: primary,
              backgroundColor: "rgba(51, 51, 51, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(51, 51, 51, 0.2)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: primary,
            mb: 0.5,
            fontSize: { xs: "1.6rem", sm: "1.8rem" },
          }}
        >
          {config.headlineTitle}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: "1rem",
            fontWeight: 400,
          }}
        >
          {config.headlineSubtitle}
        </Typography>
      </Box>

      {config.showInternalInfoBanner && (
        <InternalInfoBanner primaryColor={primary} secondaryColor={secondary} />
      )}

      <Box
        component="form"
        data-testid="register-form"
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        {chunks}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 0.5 }}>
          <Button
            variant="contained"
            disabled={submitDisabled}
            onClick={(e) => reg.handleRegister(e)}
            data-testid="register-submit"
            sx={{
              borderRadius: "0px",
              py: 1.2,
              fontSize: "1rem",
              fontWeight: 600,
              bgcolor: primary,
              color: "white",
              boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
              "&:hover": {
                bgcolor: secondary,
                boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                transform: "translateY(-1px)",
              },
              textTransform: "none",
              transition: "all 0.3s ease",
            }}
          >
            {reg.loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              config.submitButtonLabel
            )}
          </Button>

          {showSecondaryNav && (
            <Button
              variant="outlined"
              onClick={() => navigate(config.backButtonPath)}
              data-testid="register-back-to-login"
              sx={{
                borderRadius: "0px",
                py: 1,
                fontSize: "0.9rem",
                fontWeight: 600,
                borderColor: primary,
                color: primary,
                borderWidth: "2px",
                "&:hover": {
                  borderColor: secondary,
                  backgroundColor: "rgba(51, 51, 51, 0.08)",
                  borderWidth: "2px",
                },
                textTransform: "none",
              }}
            >
              Back to Login
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

RegisterStandardFormBody.propTypes = {
  reg: PropTypes.shape({
    config: PropTypes.object.isRequired,
    variantId: PropTypes.string.isRequired,
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    showPassword: PropTypes.bool.isRequired,
    setShowPassword: PropTypes.func.isRequired,
    showConfirmPassword: PropTypes.bool.isRequired,
    setShowConfirmPassword: PropTypes.func.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleRegister: PropTypes.func.isRequired,
    handleKeyDown: PropTypes.func.isRequired,
    onEmailBlurDupCheck: PropTypes.func.isRequired,
    duplicateEmailCheck: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    validateUsername: PropTypes.func.isRequired,
    validateName: PropTypes.func.isRequired,
    validatePhonenumber: PropTypes.func.isRequired,
    validatePassword: PropTypes.func.isRequired,
    validateConfirmPassword: PropTypes.func.isRequired,
    validatePhonePg: PropTypes.func.isRequired,
    effectiveToken: PropTypes.string,
  }).isRequired,
  showPgPhoneMask: PropTypes.bool,
};

RegisterStandardFormBody.defaultProps = {
  showPgPhoneMask: false,
};
